import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  subscribeProviderFormInstances,
  instancesById,
  profileSharedForRules,
  nonHfpInstanceIds,
} from '../../utils/providerFormInstances';
import { CheckCircleIcon, DownloadIcon, LockClosedIcon, RefreshIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';
import { db } from '../../../firebase';
import useReferralDocumentDownload from '../../hooks/useReferralDocumentDownload';
import {
  documentDownloads,
  evidencePackDownloads,
  EVIDENCE_PACK_KEY,
} from '../../utils/referralDocumentExport';
import { emisFilenameForRow } from '../../utils/emisFileNaming';
import { apiFetchReferralDocument, apiListReferralDocuments } from '../../utils/rmsApi';
import CopyButton from '../CopyButton';

/**
 * QuestionnaireDownloadsPanel
 * ---------------------------
 * Documents-tab panel for EMIS entry (rmsExportReferralDocument): one PDF of
 * all the school's questionnaire answers, one of all the parent or carer's,
 * each respondent's SNAP-IV separately, and each completed triage decision.
 * The Holistic Functional Profile is never included.
 *
 * Laid out like the Triage Outcome Letter panel below it on the same page —
 * title and progress in the header, then connected numbered rows with a
 * status badge and a PDF button — so the two read as one set of documents.
 *
 * Props:
 *   - orgId, patientRecordId: the referral
 *   - patientRecord: the live patient record (concern and triage decisions)
 *   - emisTicks (optional): "Added to EMIS" progress by row key
 *     ({ key: { at, byUid, byName } }), shown on the Admin Handover screen
 *     (TDI-386). Rows gain a tick only when onToggleEmisTick is provided;
 *     without it the panel renders exactly as before (Documents view).
 *   - onToggleEmisTick (optional): (key, nextTicked) => void
 *   - canTickEmis (optional): whether this viewer may change ticks (support /
 *     org admin — the server enforces the same rule)
 *   - pendingTickKey (optional): the row key whose tick is saving
 *   - showEmisNames (optional, TDI-388): each row also shows the EMIS file
 *     name the download will arrive under, with a copy button — the Admin
 *     Handover screen's separate-documents view. The names come from the
 *     shared emisFileNaming builder, so they match the backend's byte for
 *     byte.
 *   - perFormDocuments (optional, TDI-388/612): the separate-documents list
 *     (ADHD handover): one row per form, then one row per document the school
 *     or family uploaded (after SNAP-IV, before the letter), each with its
 *     EMIS name and tick. An upload downloads as its PDF copy through
 *     rmsFetchReferralDocument, behind the same confidentiality warning, and
 *     only for the roles the server lets download.
 *   - trailingRows (optional, TDI-388): extra rows rendered after the
 *     document rows in the same anatomy — the outcome letter, listed last,
 *     whose download goes through its own fetch. Each: { key, title, detail,
 *     available, filename, onDownload, downloading, hideActionsUntilAvailable? }.
 *     With hideActionsUntilAvailable, an unavailable row shows no download
 *     and no "Added to EMIS" tick (TDI-693).
 *   - evidencePack (optional, TDI-389): { pathway, emisComment } for a
 *     pathway that files one evidence pack. The rows become the pack (built
 *     by rmsExportReferralDocument with document 'evidencePack') and this
 *     pathway's triage decision, and the pack row carries the EMIS comment
 *     with its copy button. emisComment is the one the last download cached
 *     on the record; each download rebuilds the pack and refreshes it.
 */
export default function QuestionnaireDownloadsPanel({
  orgId,
  patientRecordId,
  patientRecord,
  emisTicks,
  onToggleEmisTick,
  canTickEmis = false,
  pendingTickKey = null,
  showEmisNames = false,
  // TDI-388: separate-documents pathways list one row per form (each its own
  // PDF for EMIS) instead of the combined respondent bundles.
  perFormDocuments = false,
  trailingRows = [],
  evidencePack = null,
}) {
  const [formInstances, setFormInstances] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const {
    pendingKey, downloadDocument, confirmDownload, confirmDialog,
  } = useReferralDocumentDownload({ orgId, patientRecordId, patientRecord });

  // The documents a school or family uploaded (TDI-612), on the
  // separate-documents list only. Until they load, or if they cannot be
  // read, the form rows stand on their own; a failed list is said, not hidden.
  const [uploads, setUploads] = useState([]);
  const [uploadsError, setUploadsError] = useState(false);
  const [canDownloadUploads, setCanDownloadUploads] = useState(false);
  const [uploadPendingKey, setUploadPendingKey] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!perFormDocuments || !orgId || !patientRecordId) return undefined;
    (async () => {
      try {
        const res = await apiListReferralDocuments({ orgId, patientRecordId });
        if (cancelled) return;
        setUploads(Array.isArray(res?.documents) ? res.documents : []);
        setCanDownloadUploads(Boolean(res?.canDownload));
        setUploadsError(false);
      } catch (err) {
        console.error('[QuestionnaireDownloadsPanel] Error loading uploaded documents:', err);
        if (!cancelled) setUploadsError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [perFormDocuments, orgId, patientRecordId]);

  const downloadUpload = useCallback((row) => confirmDownload(async () => {
    if (uploadPendingKey) return;
    setUploadPendingKey(row.key);
    try {
      const res = await apiFetchReferralDocument({
        orgId, patientRecordId, documentId: row.upload.id, disposition: 'attachment',
      });
      if (res?.downloadUrl) window.location.assign(res.downloadUrl);
      else toast.error('The document could not be downloaded. Please try again.');
    } catch (err) {
      toast.error(err?.message || 'The document could not be downloaded. Please try again.');
    } finally {
      setUploadPendingKey(null);
    }
  }), [confirmDownload, uploadPendingKey, orgId, patientRecordId]);

  // The form instances a provider may read (TDI-574): the whole subcollection
  // once the family has shared the profile, otherwise the non-HFP instances
  // one by one, since the rule withholds the strengths and needs answers and
  // a collection read would be refused outright.
  const profileSharedFlag = profileSharedForRules(patientRecord);
  const nonHfpIdsKey = patientRecord ? nonHfpInstanceIds(patientRecord).join(',') : '';
  useEffect(() => {
    if (!orgId || !patientRecordId || !patientRecord) return undefined;
    return subscribeProviderFormInstances(
      { orgId, patientRecordId, record: patientRecord },
      (snapshot) => {
        setFormInstances(instancesById(snapshot));
        setLoadError(false);
      },
      (err) => {
        console.error('[QuestionnaireDownloadsPanel] Error loading form instances:', err);
        setLoadError(true);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, patientRecordId, profileSharedFlag, nonHfpIdsKey]);

  const evidencePackPathway = evidencePack ? (evidencePack.pathway || null) : undefined;
  const rows = useMemo(() => {
    if (!formInstances) return [];
    // The evidence pack (autism, TDI-389/665) carries the uploads inside the
    // pack (TDI-613), so they are never rows of their own there.
    if (evidencePackPathway !== undefined) {
      return evidencePackDownloads(formInstances, patientRecord, evidencePackPathway);
    }
    return documentDownloads(formInstances, patientRecord, { perForm: perFormDocuments, uploads });
  }, [formInstances, patientRecord, perFormDocuments, evidencePackPathway, uploads]);
  // The handover screen appends the outcome letter as the last row (TDI-388:
  // "the outcome letter is its own PDF, listed last"); its download goes
  // through the letter fetch, not the export callable.
  const displayRows = useMemo(
    () => [
      ...rows.map((r) => ({ ...r, trailing: false })),
      ...(trailingRows || []).map((r) => ({ ...r, trailing: true })),
    ],
    [rows, trailingRows],
  );
  const readyCount = displayRows.filter((r) => r.available).length;
  const progressPercent = displayRows.length
    ? Math.round((readyCount / displayRows.length) * 100)
    : 0;

  let body;
  if (loadError) {
    body = (
      <p className="ml-4 mt-4 text-sm italic text-red-700">
        The questionnaires could not be loaded. Refresh the page to try again.
      </p>
    );
  } else if (formInstances === null) {
    body = (
      <p className="ml-4 mt-4 inline-flex items-center gap-2 text-sm italic text-gray-600">
        <RefreshIcon className="w-4 h-4 animate-spin" aria-hidden="true" />
        Loading questionnaires…
      </p>
    );
  } else {
    body = (
      <div className="relative mt-6">
        {/* Vertical connecting line, as on the outcome letter panel */}
        {displayRows.length > 1 && <div className="absolute left-[40px] top-6 bottom-6 w-0.5 bg-gray-200" />}

        {displayRows.map((row, index) => {
          const { key, title, detail, available, request, trailing } = row;
          const isUpload = Boolean(row.upload);
          // An upload only downloads for the roles the server allows (Tom
          // King, 31 October 2026); for anyone else its button stays off,
          // while its badge still says the document is there.
          const canDownloadRow = available && (!isUpload || canDownloadUploads);
          let preparing = pendingKey === key;
          if (trailing) preparing = Boolean(row.downloading);
          else if (isUpload) preparing = uploadPendingKey === key;
          const tick = (emisTicks || {})[key] || null;
          const tickSaving = pendingTickKey === key;
          // A row may ask for no download and no tick until it is available
          // (TDI-693: the outcome letter before sign-off), rather than a
          // disabled download beside a tick for a file nobody can file yet.
          const hideActions = !available && Boolean(row.hideActionsUntilAvailable);
          // The EMIS file name this row's download arrives under (TDI-388):
          // trailing rows carry theirs; export rows derive it from the shared
          // builder. Rows whose name cannot be known (legacy unsuffixed SNAP
          // instances) show none rather than a wrong one.
          let emisName = null;
          if (showEmisNames) {
            if (trailing) emisName = row.filename;
            else if (isUpload) emisName = emisFilenameForRow(patientRecord, key, row.upload.emisLabel);
            else emisName = emisFilenameForRow(patientRecord, key);
          }
          return (
            <div key={key} className="relative">
              <div
                className={`w-full flex items-center gap-6 my-1 py-3 px-4 rounded-lg transition-all duration-200 ${
                  available ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                }`}
              >
                {/* Numbered circle */}
                <div
                  className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center font-semibold text-lg ${
                    available
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-gray-50 text-gray-400'
                  }`}
                  aria-hidden="true"
                >
                  {index + 1}
                </div>

                <div className="flex-1 flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                      {available ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          <CheckCircleIcon className="w-3.5 h-3.5" aria-hidden="true" />
                          Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                          <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" />
                          Not available
                        </span>
                      )}
                    </div>
                    <p className="text-sm italic text-gray-700 mt-1">{detail}</p>
                    {emisName && (
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-xs text-gray-600 break-all">{emisName}</p>
                        <CopyButton value={emisName} label={`Copy the file name for ${title}`} />
                      </div>
                    )}
                    {/* The EMIS comment (TDI-389): each item in the pack and
                        the page it starts on, as the last download built it. */}
                    {evidencePack && key === EVIDENCE_PACK_KEY && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-400 uppercase tracking-wide">EMIS comment</p>
                        {evidencePack.emisComment ? (
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-sm text-gray-900 break-words">{evidencePack.emisComment}</p>
                            <CopyButton value={evidencePack.emisComment} label="Copy the EMIS comment" />
                          </div>
                        ) : (
                          <p className="text-sm italic text-gray-600 mt-1">
                            The comment appears once you&apos;ve downloaded the pack.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {/* "Added to EMIS" tick (TDI-386) — the admin team's saved
                        filing progress, stored on the record so it survives
                        leaving the screen. Only rendered when the host screen
                        provides the handler (the Admin Handover screen); the
                        Documents view never shows it. */}
                    {onToggleEmisTick && !hideActions && (
                      <label
                        title={tick?.byName ? `Added to EMIS by ${tick.byName}` : undefined}
                        className={`inline-flex items-center gap-2 px-3 py-2 min-h-[44px] text-sm font-medium rounded-md border transition-colors
                          ${tick ? 'border-green-300 bg-green-50 text-green-800' : 'border-gray-300 bg-white text-gray-700'}
                          ${canTickEmis && !tickSaving ? 'cursor-pointer' : 'cursor-not-allowed opacity-80'}`}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(tick)}
                          disabled={!canTickEmis || tickSaving}
                          onChange={() => onToggleEmisTick(key, !tick)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                          aria-label={`Added to EMIS: ${title}`}
                        />
                        {tickSaving ? 'Saving…' : 'Added to EMIS'}
                      </label>
                    )}
                    {!hideActions && (
                    <button
                      type="button"
                      onClick={() => {
                        if (trailing) row.onDownload();
                        else if (isUpload) downloadUpload(row);
                        else downloadDocument(key, request);
                      }}
                      disabled={!canDownloadRow || (trailing || isUpload ? preparing : Boolean(pendingKey))}
                      aria-label={preparing ? `Preparing ${title} PDF` : `Download ${title} as PDF`}
                      className="text-gray-900 border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center py-2 px-4 min-h-[44px] text-sm font-semibold min-w-24 rounded-md transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                      {preparing ? (
                        <RefreshIcon className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" />
                      ) : (
                        <DownloadIcon className="w-4 h-4 mr-1.5" aria-hidden="true" />
                      )}
                      {preparing ? 'Preparing…' : 'PDF'}
                    </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="max-w-6xl p-8 mx-auto mb-4 text-left bg-white rounded-xl shadow-sm border transition-all duration-200">
      {/* Header: title, and how many documents are ready, as on the letter panel */}
      <div className="flex items-center justify-between ml-4 mb-2 gap-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Questionnaire Responses</h1>
          <p className="text-sm text-gray-600 mt-1">
            Every answer exactly as given, as PDFs for adding to the clinical record.
          </p>
        </div>
        {formInstances !== null && !loadError && rows.length > 0 && (
          <div className="flex items-center gap-6">
            <div className="text-right">
              <h2 className="text-sm font-medium text-gray-700">Documents ready</h2>
              <p className="text-xs text-gray-600">
                {readyCount} of {displayRows.length} document{displayRows.length !== 1 ? 's' : ''} ready
              </p>
            </div>
            <div className="w-48 h-2 bg-gray-200 rounded-full" aria-hidden="true">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  progressPercent === 100 ? 'bg-green-500' : 'bg-primary'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}
      </div>
      {body}
      {perFormDocuments && uploadsError && (
        <p className="ml-4 mt-4 text-sm italic text-red-700">
          The uploaded documents could not be loaded. Refresh the page to try again.
        </p>
      )}
      {/* The confidentiality confirm every download passes through (TDI-484). */}
      {confirmDialog}
    </div>
  );
}
